// External Packages
import { useState, useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { MdCloudSync, MdRefresh } from "react-icons/md";
import { requestAdminAPI } from "../../api/posh-api";

// Internal Components
import { UtilityContext } from "../../context/util-context";
import Heading from "../UI/Heading";
import SearchBar from "../UI/SearchBar";
import ListLabels from "../UI/ListLabels";
import LoadingCard from "../UI/LoadingCard";
import PaginationMenu from "../UI/PaginationMenu";
import NoRecordsFound from "../UI/NoRecordsFound";
import DateFilter from "../UI/DateFilter";
import ExportCsvButton from "../UI/ExportCsvButton";
import FilterMenu from "../UI/FilterMenu";
import { isoDateToString } from "../../util/format-date";
import { formatCurrency, truncate } from "../../util/format-text";

/** ========================================================================
 * * Transactions  Page
 * Renders a page, showing a list of transactions from bank accounts.
 * ========================================================================== */
export default function Transactions() {
  const { navigate } = useContext(UtilityContext);
  const { search } = useLocation();
  const params = new URLSearchParams(search);

  // Default Filter
  const defaultFilters = {
    query: "",
    type: "all",
    bank: "all",
    reconciled: "all",
    limit: 50,
    sd: "",
    ed: "",
  };

  // Filters in URL params
  const paramFilters = {};
  Object.keys(defaultFilters).forEach(
    (key) => (paramFilters[key] = params.get(key) ?? defaultFilters[key]),
  );

  // Initialize States
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [filter, setFilter] = useState(paramFilters);

  /* ======================= FETCH RECORDS ======================= */
  const fetchRecords = async () => {
    try {
      setLoading(true);

      // Send Request
      const data = await requestAdminAPI("search", {
        table: "transaction",
        limit: filter.limit,
        filters: filter,
      });

      // Replace URL
      let url = "/transactions?";
      Object.keys(filter).forEach((key) => {
        url += `${key}=${filter[key] ?? defaultFilters[key]}&`;
      });
      navigate(url, {}, { replace: true });

      setTransactions(data);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= SYNC RECORDS ======================= */
  const syncRecords = async () => {
    try {
      setLoading(true);
      await requestAdminAPI("get-bank-transactions", {
        sheetId: "1_Pfj09lwIxnOIdgHpkC48BckvPADEmv2NbIYjYoukCs",
      });
      fetchRecords();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // Filter Options
  const filterOptions = [
    {
      label: "Bank",
      options: [
        { label: "All", value: "all" },
        { label: "Chase", value: "chase" },
        { label: "PNC", value: "pnc" },
      ],
      selected: filter.bank,
    },
    {
      label: "Type",
      options: [
        { label: "All", value: "all" },
        { label: "Expense", value: "expense" },
        { label: "Income", value: "income" },
      ],
      selected: filter.type,
    },
    {
      label: "Reconciled",
      options: [
        { label: "All", value: "all" },
        { label: "Reconciled", value: "reconciled" },
        { label: "Not Reconciled", value: "not reconciled" },
      ],
      selected: filter.reconciled,
    },
  ];

  // Calculate Total
  let total = 0;
  transactions.forEach((transaction) => {
    total += Number(transaction.amount);
  });

  useEffect(() => {
    fetchRecords();
  }, [filter]);

  return (
    <div className="container">
      <Heading title="Transactions" isBackArrow={true} hideHR={true}>
        <DateFilter
          startDate={filter.sd}
          endDate={filter.ed}
          onSubmit={(sd, ed) => setFilter({ ...filter, sd, ed })}
        />
        <FilterMenu
          onSubmit={(f) => setFilter({ ...filter, ...f })}
          options={filterOptions}
        />
        <MdCloudSync
          onClick={syncRecords}
          className="text-2xl icon"
          title="Sync Transactions"
        />
        <MdRefresh
          onClick={fetchRecords}
          className="text-2xl icon"
          title="Refresh"
        />
        <ExportCsvButton array={transactions} filename="expenses" />
      </Heading>

      <SearchBar
        searchHandler={(query) => setFilter({ ...filter, query })}
        clearSearch={() => setFilter(defaultFilters)}
        searchValue={filter.query}
      />

      {loading ? (
        <LoadingCard columns={5} max={6} />
      ) : transactions.length > 0 ? (
        <div className="gap-4 list">
          <div className="lg:grid-cols-8 labels">
            <ListLabels
              labels={[
                { name: "Date Added", key: "dateCreated" },
                { classes: "lg:col-span-3", name: "Description" },
                { name: "Transaction Date" },
                { name: "Bank" },
                { name: "Account" },
                { classes: "text-right", name: "Amount", key: "amount" },
              ]}
              selected={filter.sort}
              setSelected={(sort) => setFilter({ ...filter, sort })}
            />
          </div>
          {transactions.map((transaction, idx) => (
            <div
              key={idx}
              className={`grid grid-cols-8 pill ${transaction.reconciledById ? "border-green-600" : ""}`}
              onClick={() => navigate(`/transactions/info?id=${transaction.id}`)}
            >
              <p className="text-stone-400">
                {isoDateToString(transaction.dateCreated)}
              </p>
              <p className="lg:col-span-3 text-stone-200">
                {truncate(transaction.description, 50)}
              </p>
              <p>{transaction.date}</p>
              <p className="text-stone-400">{transaction.bank.toUpperCase()}</p>
              <p className="text-stone-400">{transaction.account}</p>
              <p
                className={`ml-auto font-semibold ${transaction.type === "expense" ? "text-red-500" : "text-green-500"}`}
              >
                {formatCurrency(transaction.amount)}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}

      <PaginationMenu
        setLimit={(limit) => setFilter({ ...filter, limit })}
        limit={filter.limit}
        loading={loading}
        count={transactions.length}
      >
        <p className="text-stone-400">
          Total:{" "}
          <span className="font-semibold text-stone-200">
            {formatCurrency(total)}
          </span>
        </p>
      </PaginationMenu>
    </div>
  );
}
